import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit, Trash2, Save, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = () => ({ title: "", description: "", embedUrl: "", thumbnail: "", duration: "" });

export default function AdminPodcastsPage() {
  const { authHeader } = useAuth();
  const [podcasts, setPodcasts] = useState([]);
  const [form, setForm] = useState(null); // null=hidden, {}=add, {id,...}=edit
  const [saving, setSaving] = useState(false);

  const fetch = async () => {
    const r = await axios.get(`${API}/podcasts`);
    setPodcasts(r.data);
  };
  useEffect(() => { fetch(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) {
        await axios.put(`${API}/admin/podcasts/${form.id}`, form, authHeader());
      } else {
        await axios.post(`${API}/admin/podcasts`, form, authHeader());
      }
      await fetch();
      setForm(null);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    await axios.delete(`${API}/admin/podcasts/${id}`, authHeader());
    setPodcasts(p => p.filter(x => x.id !== id));
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl border border-[#273A2F] text-[#F2F4F3] text-sm placeholder-[#A8BCAE] focus:outline-none focus:border-[#D97A46]/50 bg-[#0F1612]";

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-podcasts-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#F2F4F3]">Podcasts</h1>
          <p className="text-sm text-[#A8BCAE] mt-1">{podcasts.length} episodes published</p>
        </div>
        <button data-testid="add-podcast-btn" onClick={() => setForm(emptyForm())}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white" style={{ backgroundColor: '#D97A46' }}>
          <Plus className="w-4 h-4" /> Add Podcast
        </button>
      </div>

      {/* Add/Edit Form */}
      {form && (
        <div className="mb-6 p-6 rounded-2xl border border-[#D97A46]/30" style={{ backgroundColor: '#16201A' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#F2F4F3]">{form.id ? "Edit Podcast" : "Add New Podcast"}</h3>
            <button onClick={() => setForm(null)} className="text-[#A8BCAE] hover:text-[#F2F4F3]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSave} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[#A8BCAE] mb-1.5">Title *</label>
              <input className={inputCls} required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Podcast episode title" data-testid="podcast-title-input" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[#A8BCAE] mb-1.5">Description</label>
              <textarea className={inputCls} rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief episode description" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-[#A8BCAE] mb-1.5">YouTube / Spotify URL *</label>
              <input className={inputCls} required value={form.embedUrl} onChange={e => setForm(f => ({ ...f, embedUrl: e.target.value }))} placeholder="https://www.youtube.com/watch?v=..." data-testid="podcast-url-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#A8BCAE] mb-1.5">Thumbnail URL</label>
              <input className={inputCls} value={form.thumbnail} onChange={e => setForm(f => ({ ...f, thumbnail: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#A8BCAE] mb-1.5">Duration</label>
              <input className={inputCls} value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} placeholder="e.g. 24 min" />
            </div>
            <div className="col-span-2 flex gap-3">
              <button type="button" onClick={() => setForm(null)} className="px-5 py-2 rounded-xl border border-[#273A2F] text-sm text-[#A8BCAE]">Cancel</button>
              <button type="submit" data-testid="save-podcast-btn" disabled={saving}
                className="flex items-center gap-2 px-6 py-2 rounded-xl text-sm font-medium text-white disabled:opacity-60" style={{ backgroundColor: '#D97A46' }}>
                <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Podcast List */}
      <div className="space-y-3">
        {podcasts.map(p => (
          <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl border border-[#273A2F]" style={{ backgroundColor: '#16201A' }} data-testid={`podcast-row-${p.id}`}>
            {p.thumbnail && <img src={p.thumbnail} alt={p.title} className="w-16 h-12 rounded-lg object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#F2F4F3] text-sm truncate">{p.title}</p>
              <p className="text-xs text-[#A8BCAE] truncate mt-0.5">{p.embedUrl}</p>
              {p.duration && <span className="text-xs text-[#D97A46]">{p.duration}</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setForm(p)} className="p-2 rounded-lg text-[#A8BCAE] hover:text-[#F2F4F3] hover:bg-white/5 transition-all"><Edit className="w-4 h-4" /></button>
              <button onClick={() => handleDelete(p.id, p.title)} className="p-2 rounded-lg text-[#A8BCAE] hover:text-red-400 hover:bg-red-400/10 transition-all"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {podcasts.length === 0 && <div className="text-center py-12 text-[#A8BCAE] text-sm">No podcasts yet. Add your first one!</div>}
      </div>
    </div>
  );
}
